// Core Annex System (owner spec 2026-07-13): socketed Core annexes and everything
// they grow — periodic spawners with stored-spawn Command handling, Blood Tithe and
// the Apotheosis, Echo Charges, Deep Recall channels, worker transfers, the Phase
// Barrier shield, rooted Rootwarden auras, deployed-structure withering, and forward
// building spawners (Grave Beacon). Faction-agnostic: everything is keyed off typed
// content data (UpgradeDef/UnitDef/BuildingDef fields), never faction ids.

import { buildingDef, unitDef } from '../../content/registry';
import type { BuildingDef, BuildingPassive, PlayerId, UpgradeDef } from '../../content/types';
import { TUNING } from '../../content/tuning';
import { dist, vec } from '../../shared/vec';
import type { Sim } from '../sim';
import type { Building, PlayerState, WorkerEnt } from '../state';
import { footprintOf } from '../state';

const dt = TUNING.dt;

/** All area passives of a building (single `passive` plus the `passives` list). */
export function buildingPassives(bd: BuildingDef): BuildingPassive[] {
  const list: BuildingPassive[] = [];
  if (bd.passive) list.push(bd.passive);
  if (bd.passives) list.push(...bd.passives);
  return list;
}

export function stepAnnexes(sim: Sim): void {
  const st = sim.state;
  for (const p of st.players) {
    // -------- timers
    if (p.annex.deepRecallCooldown > 0) p.annex.deepRecallCooldown -= dt;
    if (p.annex.transferCooldown > 0) p.annex.transferCooldown -= dt;
    if (p.annex.deathSaveCooldown > 0) p.annex.deathSaveCooldown -= dt;
    for (let i = p.annex.grimStacks.length - 1; i >= 0; i--) {
      p.annex.grimStacks[i] -= dt;
      if (p.annex.grimStacks[i] <= 0) p.annex.grimStacks.splice(i, 1);
    }

    // -------- Phase Barrier: regenerating core shield
    const shield = sim.annexEffect(p.id, 'coreShield');
    if (shield) {
      p.annex.coreShield = Math.min(shield.amount, p.annex.coreShield + shield.regenPerSecond * dt);
    } else {
      p.annex.coreShield = 0;
    }

    // -------- socketed annex behaviors
    for (const up of sim.coreAnnexes(p.id)) {
      if (up.spawner) stepSpawner(sim, p, up.id, up.spawner);
      if (up.bloodTithe) stepTithe(sim, p, up.bloodTithe);
      if (up.echo && p.annex.echoCharge < 1) {
        p.annex.echoProgress += dt;
        if (p.annex.echoProgress >= up.echo.period) {
          p.annex.echoProgress = 0;
          p.annex.echoCharge = 1;
        }
      }
    }

    stepApotheosis(sim, p);
  }

  stepSquadTimers(sim);
  stepWorkerChannels(sim);
  stepBuildings(sim);
}

// ---------------------------------------------------------------- spawners

function stepSpawner(
  sim: Sim,
  p: PlayerState,
  upgradeId: string,
  spawner: { unitId: string; period: number; maxAlive: number },
): void {
  const annex = p.annex;
  let alive = 0;
  for (const s of sim.state.squads.values()) {
    if (s.owner === p.id && !s.dead && s.sourceAnnexId === upgradeId) alive++;
  }

  // release a stored spawn once Command frees up (one stored per annex)
  if (annex.stored[upgradeId]) {
    if (alive < spawner.maxAlive && releaseSpawn(sim, p, upgradeId, annex.stored[upgradeId])) {
      delete annex.stored[upgradeId];
      annex.spawnTimers[upgradeId] = spawner.period;
    }
    return; // the annex holds its one finished spawn; the cycle waits
  }

  // the growth cycle pauses at the alive cap
  if (alive >= spawner.maxAlive) return;

  const t = (annex.spawnTimers[upgradeId] ?? spawner.period) - dt;
  if (t > 0) {
    annex.spawnTimers[upgradeId] = t;
    return;
  }
  if (releaseSpawn(sim, p, upgradeId, spawner.unitId)) {
    annex.spawnTimers[upgradeId] = spawner.period;
  } else {
    // Command is full: the annex stores the completed spawn (max one)
    annex.stored[upgradeId] = spawner.unitId;
    annex.spawnTimers[upgradeId] = spawner.period;
  }
}

function releaseSpawn(sim: Sim, p: PlayerState, upgradeId: string, unitId: string): boolean {
  const core = sim.coreAlive(p.id);
  if (!core) return false;
  const d = unitDef(unitId);
  if (d.command > 0 && p.commandUsed + d.command > p.commandCap) return false;
  const fp = footprintOf(buildingDef(core.defId), core.rot);
  const door = vec(core.pos.x, core.pos.y - fp.h / 2 - 2);
  const squad = sim.spawnSquad(unitId, p.id, door, { sourceAnnexId: upgradeId });
  // Core annex output uses the same rally contract as every production building.
  sim.sendSpawnToRally(squad, core);
  sim.pushEvent({
    type: 'AbilityTriggered',
    id: squad.id,
    abilityId: 'annex-spawn',
    pos: { ...squad.pos },
    label: d.name,
  });
  return true;
}

// ---------------------------------------------------------------- Blood Tithe

type TitheSpec = NonNullable<UpgradeDef['bloodTithe']>;

function stepTithe(sim: Sim, p: PlayerState, spec: TitheSpec): void {
  const annex = p.annex;
  if (!annex.titheActive || annex.apotheosisUsed || annex.apotheosis) return;
  const core = sim.coreAlive(p.id);
  if (!core) return;

  // pauses automatically while the workforce is too thin
  let workers = 0;
  for (const w of sim.state.workers.values()) if (w.owner === p.id && !w.dead) workers++;
  if (workers < spec.minWorkers) return;

  annex.titheTimer -= dt;
  if (annex.titheTimer <= spec.warnSeconds && !annex.titheWarned) {
    annex.titheWarned = true;
    sim.pushEvent({
      type: 'AbilityTriggered',
      id: core.id,
      abilityId: 'tithe-warning',
      pos: { ...core.pos },
      label: 'Blood Tithe imminent',
    });
  }
  if (annex.titheTimer > 0) return;

  const victim = titheVictim(sim, p.id);
  if (!victim) {
    annex.titheTimer = spec.period; // nobody unsheltered — try again next cycle
    annex.titheWarned = false;
    return;
  }
  const at = { ...victim.pos };
  sim.damageWorker(victim, victim.health + 10000); // permanent — never refunded
  annex.souls++;
  annex.titheTimer = spec.period;
  annex.titheWarned = false;
  sim.grantGrimIndustry(p.id);
  sim.pushEvent({
    type: 'AbilityTriggered',
    id: core.id,
    abilityId: 'blood-tithe',
    pos: at,
    label: `Souls Offered ${annex.souls}/${spec.soulsRequired}`,
  });

  if (annex.souls >= spec.soulsRequired) {
    annex.titheActive = false;
    const cocoonDef = buildingDef(spec.cocoonBuildingId);
    const cfp = footprintOf(buildingDef(core.defId), core.rot);
    // first clear plot ringing the Core; the cocoon must be reachable (counterplay)
    let at2 = vec(core.pos.x, core.pos.y + cfp.h / 2 + cocoonDef.footprint.h / 2 + 2);
    for (const [ox, oy] of [
      [0, 1],
      [1, 0],
      [-1, 0],
      [0, -1],
    ] as const) {
      const cand = vec(
        core.pos.x + ox * (cfp.w / 2 + cocoonDef.footprint.w / 2 + 2),
        core.pos.y + oy * (cfp.h / 2 + cocoonDef.footprint.h / 2 + 2),
      );
      if (sim.footprintClear(cand, cocoonDef.footprint)) {
        at2 = cand;
        break;
      }
    }
    const cocoon = sim.spawnBuilding(spec.cocoonBuildingId, p.id, at2, {
      complete: true,
      annexSourceId: 'apotheosis',
    });
    annex.apotheosis = { buildingId: cocoon.id, remaining: spec.apotheosisSeconds };
    sim.pushEvent({
      type: 'AbilityTriggered',
      id: cocoon.id,
      abilityId: 'apotheosis-begin',
      pos: { ...at2 },
      label: 'Apotheosis begins',
    });
  }
}

/** Spec priority: idle workers first, then the worker farthest from a deposit. */
function titheVictim(sim: Sim, owner: PlayerId): WorkerEnt | null {
  let idle: WorkerEnt | null = null;
  let far: WorkerEnt | null = null;
  let farDist = -1;
  for (const w of sim.state.workers.values()) {
    if (w.owner !== owner || w.dead || w.state === 'sheltered') continue;
    if (w.state === 'idle') {
      if (!idle || w.id < idle.id) idle = w;
      continue;
    }
    let nearest = Infinity;
    for (const dep of sim.state.deposits.values()) nearest = Math.min(nearest, dist(w.pos, dep.pos));
    if (nearest > farDist || (nearest === farDist && far && w.id < far.id)) {
      farDist = nearest;
      far = w;
    }
  }
  return idle ?? far;
}

function stepApotheosis(sim: Sim, p: PlayerState): void {
  const annex = p.annex;
  if (!annex.apotheosis) return;
  const cocoon = sim.state.buildings.get(annex.apotheosis.buildingId);
  if (!cocoon || cocoon.state === 'destroyed') {
    // counterplay: cocoon destroyed — the five workers stay lost, the Apotheosis fails
    annex.apotheosis = null;
    annex.apotheosisUsed = true;
    return;
  }
  annex.apotheosis.remaining -= dt;
  if (annex.apotheosis.remaining > 0) return;

  const spec = [...sim.coreAnnexes(p.id)].find((u) => u.bloodTithe)?.bloodTithe;
  const pos = { ...cocoon.pos };
  sim.destroyBuildingSilently(cocoon);
  annex.apotheosis = null;
  annex.apotheosisUsed = true; // only one Apotheosis per match
  if (!spec) return;
  const form = spec.forms.find((f) => f.form === annex.apotheosisForm) ?? spec.forms[0];
  sim.spawnSquad(form.unitId, p.id, pos);
  sim.pushEvent({ type: 'BossSummoned', player: p.id, defId: form.unitId, pos });
}

// ---------------------------------------------------------------- Astral Echo

/**
 * Called where squads finish production or are summoned by a unit card. Spends the
 * stored Echo Charge if the location is served by a connected Conduit.
 */
export function maybeEchoSquad(sim: Sim, owner: PlayerId, defId: string, pos: { x: number; y: number }): void {
  const p = sim.state.players[owner];
  const spec = [...sim.coreAnnexes(owner)].find((u) => u.echo)?.echo;
  if (!spec || !p.annex.echoArmed || p.annex.echoCharge < 1) return;
  const d = unitDef(defId);
  if (d.isBoss || d.workerVariant || d.roleTags.includes('worker')) return;
  // "at a connected location": the Core or any connected conduit near the spawn
  let conduitId: number | null = null;
  for (const b of sim.state.buildings.values()) {
    if (b.owner !== owner || b.state !== 'active') continue;
    if (!buildingDef(b.defId).conduit) continue;
    if (!b.isCore && !sim.state.connectedConduits.has(b.id)) continue;
    if (dist(pos, b.pos) <= 14) {
      conduitId = b.id;
      break;
    }
  }
  if (conduitId === null) return;

  p.annex.echoCharge = 0;
  const echo = sim.spawnSquad(defId, owner, vec(pos.x + 2, pos.y + 2), {
    maxHealthMult: spec.healthMult,
    temporarySeconds: spec.duration,
    commandFree: true, // spec: costs no Command
    isEcho: true, // spec: cannot capture sites
    echoLink: { conduitId, remaining: spec.linkSeconds },
  });
  // 60% damage, half damage to Cores/major structures — permanent buffs on the copy
  echo.buffs.push({ stat: 'damage', mult: spec.damageMult, remaining: Infinity, source: 'astral-echo' });
  echo.buffs.push({ stat: 'structureDamage', mult: spec.structureDamageMult, remaining: Infinity, source: 'astral-echo' });
  echo.command = { kind: 'defend', pointA: { ...echo.pos }, auto: true };
  sim.pushEvent({
    type: 'AbilityTriggered',
    id: echo.id,
    abilityId: 'astral-echo',
    pos: { ...echo.pos },
    label: 'Astral Echo',
  });
}

// ---------------------------------------------------------------- squad timers

function stepSquadTimers(sim: Sim): void {
  for (const s of sim.state.squads.values()) {
    // uproot channel → aura suppression window
    if (s.uprootRemaining > 0) {
      s.uprootRemaining -= dt;
      if (s.uprootRemaining <= 0) {
        s.uprootRemaining = 0;
        s.treeRooted = false;
        s.auraSuppressedRemaining = unitDef(s.defId).rootable?.auraSuppressedSeconds ?? 0;
      }
    }
    if (s.auraSuppressedRemaining > 0) s.auraSuppressedRemaining -= dt;

    // echo umbilical: dies with its arrival conduit during the early window
    if (s.echoLink) {
      s.echoLink.remaining -= dt;
      if (s.echoLink.remaining <= 0) {
        s.echoLink = null;
      } else if (!sim.state.buildings.has(s.echoLink.conduitId)) {
        sim.despawnSquad(s);
        continue;
      }
    }

    // rooted Rootwarden: worker healing aura (gather/retreat buffs applied in workers.ts)
    const rootable = unitDef(s.defId).rootable;
    if (rootable && s.treeRooted && s.uprootRemaining <= 0 && s.auraSuppressedRemaining <= 0) {
      for (const w of sim.state.workers.values()) {
        if (w.owner !== s.owner || w.dead || w.state === 'sheltered') continue;
        if (dist(w.pos, s.pos) > rootable.auraRadius) continue;
        w.health = Math.min(w.maxHealth, w.health + rootable.workerHealPerSecond * dt);
      }
    }
  }
}

/** Rooted-guardian economy multipliers near this worker (1 when none apply). */
export function rootAuraMults(sim: Sim, w: WorkerEnt): { gather: number; retreat: number } {
  let gather = 1;
  let retreat = 1;
  for (const s of sim.state.squads.values()) {
    if (s.owner !== w.owner || !s.treeRooted || s.uprootRemaining > 0 || s.auraSuppressedRemaining > 0) continue;
    const rootable = unitDef(s.defId).rootable;
    if (!rootable) continue;
    if (dist(w.pos, s.pos) > rootable.auraRadius) continue;
    gather = Math.max(gather, rootable.gatherMult);
    retreat = Math.max(retreat, rootable.retreatSpeedMult);
  }
  return { gather, retreat };
}

// ---------------------------------------------------------------- worker channels

function stepWorkerChannels(sim: Sim): void {
  for (const w of sim.state.workers.values()) {
    if (w.arrivalRemaining > 0) w.arrivalRemaining -= dt;
    if (w.recallChannelRemaining > 0) {
      w.recallChannelRemaining -= dt;
      if (w.recallChannelRemaining <= 0) {
        w.recallChannelRemaining = 0;
        burrowToShelter(sim, w);
      }
    }
  }
}

/** Deep Recall completion: the worker emerges inside the Core shelter. */
function burrowToShelter(sim: Sim, w: WorkerEnt): void {
  const p = sim.state.players[w.owner];
  const core = sim.coreAlive(w.owner);
  if (!core || p.shelterOccupants.length >= p.shelterCapacity) return;
  // bank whatever it carries on the way underground
  if (w.carrying) {
    if (w.carrying.type === 'power') p.power += w.carrying.amount;
    else p.essence += w.carrying.amount;
    w.carrying = null;
  }
  w.pos = { ...core.pos };
  w.prevPos = { ...core.pos };
  w.path = null;
  w.depositId = null;
  w.state = 'sheltered';
  p.shelterOccupants.push(w.id);
}

// ---------------------------------------------------------------- buildings

function stepBuildings(sim: Sim): void {
  for (const b of [...sim.state.buildings.values()]) {
    // replaced annex structures wither away
    if (b.witherRemaining !== null) {
      b.witherRemaining -= dt;
      if (b.witherRemaining <= 0) {
        sim.destroyBuilding(b);
        continue;
      }
    }
    if (b.state !== 'active') continue;
    const bd = buildingDef(b.defId);

    // worker healing aura (Bramblehold)
    for (const passive of buildingPassives(bd)) {
      if (passive.kind !== 'workerHealAura') continue;
      for (const w of sim.state.workers.values()) {
        if (w.owner !== b.owner || w.dead || w.state === 'sheltered') continue;
        if (dist(w.pos, b.pos) > passive.radius) continue;
        w.health = Math.min(w.maxHealth, w.health + passive.perSecond * dt);
      }
    }

    // forward-structure spawner (Grave Beacon Ghoul Packs)
    const spawner = bd.spawner;
    if (!spawner) continue;
    if (spawner.corpseCost) {
      const cap = spawner.corpseCost * 2;
      const radius = spawner.corpseAbsorbRadius ?? 14;
      for (let i = sim.state.corpseMarks.length - 1; i >= 0 && b.counter < cap; i--) {
        const mark = sim.state.corpseMarks[i];
        if (dist(mark.pos, b.pos) > radius) continue;
        sim.state.corpseMarks.splice(i, 1);
        b.counter++;
      }
    }
    if (b.spawnCooldown > 0) {
      b.spawnCooldown -= dt;
      continue;
    }
    if (spawner.corpseCost && b.counter < spawner.corpseCost) continue; // waiting for grave energy
    if (spawner.corpseCost) b.counter -= spawner.corpseCost;
    b.spawnCooldown = spawner.period;
    const fp = footprintOf(bd, b.rot);
    for (let i = 0; i < spawner.count; i++) {
      const squad = sim.spawnSquad(spawner.unitId, b.owner, vec(b.pos.x + i * 1.5, b.pos.y + fp.h / 2 + 2), {
        temporarySeconds: spawner.temporarySeconds,
        sourceAnnexId: `bspawn-${b.id}`,
      });
      if (spawner.command === 'raid') {
        // spec: automatically attack the nearest enemy-controlled site
        let target = null as { x: number; y: number } | null;
        let best = Infinity;
        for (const site of sim.state.sites) {
          if (site.owner === -1 || !sim.areHostile(site.owner, b.owner)) continue;
          const d2 = dist(site.pos, b.pos);
          if (d2 < best) {
            best = d2;
            target = site.pos;
          }
        }
        if (!target) target = sim.enemiesOf(b.owner).map((enemy) => sim.coreAlive(enemy)).find(Boolean)?.pos ?? null;
        squad.command = target
          ? { kind: 'raid', targetPos: { ...target } }
          : { kind: 'defend', pointA: { ...squad.pos }, auto: true };
      } else {
        squad.command = { kind: 'defend', pointA: { ...squad.pos }, auto: true };
      }
    }
    sim.pushEvent({
      type: 'AbilityTriggered',
      id: b.id,
      abilityId: 'beacon-wave',
      pos: { ...b.pos },
      label: unitDef(spawner.unitId).name,
    });
  }
}
